/**
 * Engineer Mode — remote configuration. Two governed session types share one lifecycle (confirm,
 * reason, TTL, transcript, history):
 *   - register_write (Tier A, §7A): write Modbus-writable parameters with read-back verification.
 *   - config_suite   (Tier B, §7B, Model A): a governed window for DSE Configuration Suite over the
 *     site tunnel. No register writes here; the audit is the window + (later) before/after config
 *     snapshots. The live tunnel automation is hardware-gated — the session governs it once the RUT
 *     is commissioned. Engineer+ only.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth';
import {
  ApiError,
  type ConfigArtefact,
  type ConnectionInfo,
  type ControllerPanel,
  type EngSessionDetail,
  type EngSessionSummary,
  type EngSessionType,
  type EngWriteResult,
  type WritableParam,
} from '../api';
import { Banner, Button } from '../ui';

const typeLabel = (t: EngSessionType) => (t === 'config_suite' ? 'Config Suite' : 'parameters');

const fmtBytes = (n: number) =>
  n < 1024
    ? `${n} B`
    : n < 1024 * 1024
      ? `${(n / 1024).toFixed(1)} KB`
      : `${(n / 1048576).toFixed(1)} MB`;

/** Read a File as raw base64 (strip the data: URL prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error('could not read the file'));
    r.onload = () => {
      const res = String(r.result);
      const comma = res.indexOf(',');
      resolve(comma >= 0 ? res.slice(comma + 1) : res);
    };
    r.readAsDataURL(file);
  });
}

export function EngineerPanel(): JSX.Element {
  const { api, session } = useAuth();
  const { id = '' } = useParams();
  const [ctl, setCtl] = useState<ControllerPanel['controller'] | null>(null);
  const [params, setParams] = useState<WritableParam[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [openType, setOpenType] = useState<EngSessionType>('register_write');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionType, setSessionType] = useState<EngSessionType>('register_write');
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [edit, setEdit] = useState<Record<number, string>>({});
  const [log, setLog] = useState<EngWriteResult[]>([]);
  const [busy, setBusy] = useState(false);
  const [closed, setClosed] = useState<{ n: number; type: EngSessionType } | null>(null);
  const [artefacts, setArtefacts] = useState<{
    pre: ConfigArtefact | null;
    post: ConfigArtefact | null;
  }>({ pre: null, post: null });
  const [uploading, setUploading] = useState<'pre' | 'post' | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [connection, setConnection] = useState<ConnectionInfo | null>(null);
  const [history, setHistory] = useState<EngSessionSummary[]>([]);
  const [expanded, setExpanded] = useState<Record<string, EngSessionDetail | 'loading'>>({});

  const canEngineer = !!session && (session.role === 'engineer' || session.role === 'tenant_admin');

  const loadParams = useCallback(async () => {
    try {
      const [p, w, h] = await Promise.all([
        api.getControllerPanel(id),
        api.listWritable(id),
        api.listEngSessions(id),
      ]);
      setCtl(p.controller);
      setParams(w.params);
      setHistory(h.sessions);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not load the controller.');
    }
  }, [api, id]);

  async function toggle(sid: string) {
    if (expanded[sid]) {
      setExpanded((e) => {
        const { [sid]: _drop, ...rest } = e;
        return rest;
      });
      return;
    }
    setExpanded((e) => ({ ...e, [sid]: 'loading' }));
    try {
      const d = await api.getEngSession(sid);
      setExpanded((e) => ({ ...e, [sid]: d }));
    } catch {
      setExpanded((e) => {
        const { [sid]: _drop, ...rest } = e;
        return rest;
      });
    }
  }

  useEffect(() => {
    if (canEngineer) void loadParams();
  }, [canEngineer, loadParams]);

  if (!canEngineer)
    return <Banner kind="err">Engineer access is required to configure a controller.</Banner>;
  if (!ctl) return err ? <Banner kind="err">{err}</Banner> : <div className="empty">Loading…</div>;

  async function openSession() {
    setBusy(true);
    setErr(null);
    setClosed(null);
    try {
      const r = await api.openEngSession(id, reason.trim(), confirm.trim(), openType);
      setSessionId(r.sessionId);
      setSessionType(r.type);
      setExpiresAt(r.expiresAt);
      setLog([]);
      setArtefacts({ pre: null, post: null });
      setConnection(null);
      setConfirmClose(false);
      setConfirm('');
      if (r.type === 'config_suite') {
        try {
          setConnection(await api.getConnection(id));
        } catch {
          setConnection(null);
        }
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not open the session.');
    } finally {
      setBusy(false);
    }
  }

  async function write(p: WritableParam) {
    const raw = edit[p.address];
    if (raw === undefined || raw === '') return;
    const value = Number(raw);
    if (!Number.isInteger(value)) return;
    setErr(null);
    try {
      const res = await api.writeParam(sessionId!, p.address, value);
      setLog((l) => [res, ...l]);
      setParams((ps) =>
        ps.map((x) => (x.address === p.address ? { ...x, current: res.value_readback } : x)),
      );
      setEdit((e) => ({ ...e, [p.address]: '' }));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Write failed.');
    }
  }

  async function uploadArtefact(kind: 'pre' | 'post', file: File) {
    if (!sessionId) return;
    setUploading(kind);
    setErr(null);
    try {
      const b64 = await fileToBase64(file);
      const a = await api.uploadConfigArtefact(sessionId, kind, file.name, b64);
      setArtefacts((s) => ({ ...s, [kind]: a }));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Upload failed.');
    } finally {
      setUploading(null);
    }
  }

  async function downloadArtefact(artefactId: string) {
    try {
      const a = await api.getConfigArtefact(artefactId);
      const bytes = Uint8Array.from(atob(a.contentBase64), (ch) => ch.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes]));
      const el = document.createElement('a');
      el.href = url;
      el.download = a.filename;
      el.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Download failed.');
    }
  }

  function downloadWgProfile() {
    if (!connection?.wireguard.config) return;
    const url = URL.createObjectURL(
      new Blob([connection.wireguard.config], { type: 'text/plain' }),
    );
    const el = document.createElement('a');
    el.href = url;
    el.download = `sentiq-${ctl?.site_code || 'site'}.conf`;
    el.click();
    URL.revokeObjectURL(url);
  }

  async function close(force = false) {
    if (!sessionId) return;
    // Soft nudge: closing a config session freezes it — snapshots can only be attached while open.
    // Warn (don't block) if the before/after pair is incomplete.
    if (force !== true && sessionType === 'config_suite' && !(artefacts.pre && artefacts.post)) {
      setConfirmClose(true);
      return;
    }
    setConfirmClose(false);
    setBusy(true);
    const wasType = sessionType;
    try {
      const r = await api.closeEngSession(sessionId);
      setClosed({ n: r.changes.length, type: wasType });
      setSessionId(null);
      setExpiresAt(null);
      void loadParams();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not close the session.');
    } finally {
      setBusy(false);
    }
  }

  const shown = params.filter(
    (p) =>
      !filter ||
      p.label.toLowerCase().includes(filter.toLowerCase()) ||
      p.key.includes(filter.toLowerCase()),
  );
  // Bucket the visible params by their config group (ungrouped ones fall under '_', rendered
  // without a heading). Insertion order preserves the profile's register order within a group.
  const grouped: [string, WritableParam[]][] = (() => {
    const map = new Map<string, WritableParam[]>();
    for (const p of shown) {
      const g = p.group ?? '_';
      (map.get(g) ?? map.set(g, []).get(g)!).push(p);
    }
    return [...map.entries()];
  })();
  const confirmOk = confirm.trim().toLowerCase() === ctl.site_name.trim().toLowerCase();
  const isConfig = sessionType === 'config_suite';

  return (
    <>
      <div className="crumbs">
        <Link to={`/controllers/${id}`}>{ctl.display_name ?? 'Controller'}</Link>{' '}
        <span className="faint">/</span> engineer mode
      </div>
      <div className="page-head">
        <div>
          <h1>Remote configuration</h1>
          <div className="lead">
            {ctl.profile_name} ·{' '}
            {sessionId
              ? isConfig
                ? 'Config Suite session'
                : 'governed parameter writes'
              : 'parameter writes · full configuration via Config Suite'}
          </div>
        </div>
        {sessionId && (
          <div className="row-right">
            <span className="pill warn">
              <span className="dot" />
              {isConfig ? 'config session open' : 'session open'}
              {expiresAt ? ` · expires ${new Date(expiresAt).toLocaleTimeString()}` : ''}
            </span>
            <Button className="btn btn-ghost" busy={busy} onClick={() => void close()}>
              Close session
            </Button>
          </div>
        )}
      </div>

      {err && <Banner kind="err">{err}</Banner>}
      {closed && (
        <Banner kind="ok">
          {closed.type === 'config_suite'
            ? 'Configuration session closed — the window is logged in the history below.'
            : `Session closed — ${closed.n} change${closed.n === 1 ? '' : 's'} written and read back.`}
        </Banner>
      )}
      {confirmClose && (
        <Banner kind="warn">
          <div style={{ marginBottom: 10 }}>
            {!artefacts.pre && !artefacts.post
              ? 'No before/after config snapshot is attached.'
              : !artefacts.pre
                ? 'No as-found (before) snapshot is attached.'
                : 'No as-left (after) snapshot is attached.'}{' '}
            Snapshots can only be attached while the session is open — closing freezes it. Close
            anyway?
          </div>
          <div className="row-right">
            <button className="btn btn-ghost btn-sm" onClick={() => setConfirmClose(false)}>
              Keep session open
            </button>
            <Button className="btn btn-sm" busy={busy} onClick={() => void close(true)}>
              Close anyway
            </Button>
          </div>
        </Banner>
      )}

      {!sessionId ? (
        <div className="panel panel-pad">
          <div className="section-label">Open an engineering session</div>
          <div className="field">
            <label>Session type</label>
            <div className="seg">
              <button
                type="button"
                className={openType === 'register_write' ? 'seg-on' : ''}
                onClick={() => setOpenType('register_write')}
              >
                Parameter writes
                <span className="seg-sub">Tier A · Modbus-writable registers</span>
              </button>
              <button
                type="button"
                className={openType === 'config_suite' ? 'seg-on' : ''}
                onClick={() => setOpenType('config_suite')}
              >
                Full configuration
                <span className="seg-sub">Tier B · DSE Configuration Suite</span>
              </button>
            </div>
          </div>
          <p className="muted" style={{ marginTop: 0 }}>
            {openType === 'config_suite' ? (
              <>
                Opens a governed window for DSE Configuration Suite over the site tunnel. The window
                is transcript-logged and auto-expires. Type the site name <b>{ctl.site_name}</b> to
                confirm.
              </>
            ) : (
              <>
                Writes are only permitted inside a session — each is pre-read, applied, and read
                back to verify. The session is transcript-logged and auto-expires. Type the site
                name <b>{ctl.site_name}</b> to confirm.
              </>
            )}
          </p>
          <div className="field">
            <label htmlFor="reason">Reason</label>
            <input
              id="reason"
              className="input"
              placeholder={
                openType === 'config_suite'
                  ? 'e.g. adjust crank timers and OP shutdown threshold'
                  : 'e.g. reset service interval to 500 h'
              }
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="cf">Confirm site name</label>
            <input
              id="cf"
              className="input mono"
              placeholder={ctl.site_name}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          <div className="form-actions">
            <span />
            <Button
              busy={busy}
              disabled={reason.trim().length < 3 || !confirmOk}
              onClick={() => void openSession()}
            >
              {openType === 'config_suite' ? 'Open configuration session' : 'Open session'}
            </Button>
          </div>
        </div>
      ) : isConfig ? (
        /* ---- config_suite: a governed window for DSE Configuration Suite ---- */
        <div className="panel panel-pad">
          <div className="section-label">Configuration session · DSE Configuration Suite</div>
          <p className="muted" style={{ marginTop: 0 }}>
            Full-configuration access — the entire parameter tree (timers, shutdown thresholds,
            service intervals, voltage/frequency windows), edited in DSE Configuration Suite over
            the site's secure tunnel. This window brackets the change with who, when and why, plus a
            hashed before/after config snapshot below.
          </p>
          <ol className="conn-steps">
            <li>The secure tunnel to this controller opens for the session window.</li>
            <li>
              In DSE Configuration Suite, connect <b>over Ethernet</b> to the controller's site IP.
            </li>
            <li>Read the configuration, make your changes, and write them back.</li>
            <li>
              <b>Close the session</b> when done — the tunnel is torn down and the window is logged.
            </li>
          </ol>

          {connection && (
            <div className="conn-box">
              <div className="spread">
                <div>
                  <div className="conn-cap">Config Suite target</div>
                  {connection.configSuite.ready ? (
                    <div className="mono" style={{ fontSize: 15 }}>
                      {connection.configSuite.host}:{connection.configSuite.port}
                    </div>
                  ) : (
                    <div className="faint">controller IP not set — pending commissioning</div>
                  )}
                </div>
                {connection.configSuite.ready && (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() =>
                      void navigator.clipboard?.writeText(
                        `${connection.configSuite.host}:${connection.configSuite.port}`,
                      )
                    }
                  >
                    Copy
                  </button>
                )}
              </div>
              <div className="spread" style={{ marginTop: 12 }}>
                <div>
                  <div className="conn-cap">WireGuard tunnel</div>
                  {connection.wireguard.ready ? (
                    <div className="faint">
                      Client profile ready — add your own private key after downloading.
                    </div>
                  ) : (
                    <div className="faint">pending: {connection.wireguard.pending.join(', ')}</div>
                  )}
                </div>
                {connection.wireguard.ready && (
                  <button className="btn btn-ghost btn-sm" onClick={downloadWgProfile}>
                    Download profile
                  </button>
                )}
              </div>
            </div>
          )}

          <Banner kind="ok">
            Opening a Config Suite session opens the engineer gate at the site router and closes it
            again when the session ends or its time expires. Every parameter change made through it
            is recorded against this session.
          </Banner>

          <div className="section-label" style={{ marginTop: 20 }}>
            Config snapshots
          </div>
          <p className="muted" style={{ marginTop: 0 }}>
            Attach the Configuration Suite export <b>as-found</b> before you change anything and{' '}
            <b>as-left</b> when you finish. Each is hashed (SHA-256) so the change is provable, and
            the file is kept for audit and rollback.
          </p>
          <div className="artefact-slots">
            {(['pre', 'post'] as const).map((k) => (
              <div className="artefact-slot" key={k}>
                <div className="title">{k === 'pre' ? 'As-found (before)' : 'As-left (after)'}</div>
                {artefacts[k] && (
                  <div className="meta mono">
                    {artefacts[k]!.filename} · {fmtBytes(artefacts[k]!.byte_size)}
                    <br />
                    sha256 {artefacts[k]!.sha256.slice(0, 12)}…
                  </div>
                )}
                <label className="btn btn-ghost btn-sm file-btn">
                  {uploading === k ? 'Uploading…' : artefacts[k] ? 'Replace file' : 'Choose file'}
                  <input
                    type="file"
                    hidden
                    disabled={uploading !== null}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.currentTarget.value = '';
                      if (f) void uploadArtefact(k, f);
                    }}
                  />
                </label>
              </div>
            ))}
          </div>
          {artefacts.pre && artefacts.post && (
            <div style={{ marginTop: 10 }}>
              <span
                className={`pill ${
                  artefacts.pre.sha256 === artefacts.post.sha256 ? 'good' : 'warn'
                }`}
              >
                <span className="dot" />
                {artefacts.pre.sha256 === artefacts.post.sha256
                  ? 'before and after identical — no change'
                  : 'configuration changed'}
              </span>
            </div>
          )}
        </div>
      ) : (
        /* ---- register_write: Tier A writable parameters ---- */
        <>
          <div className="panel panel-pad" style={{ marginBottom: 16 }}>
            <div className="spread" style={{ marginBottom: 12 }}>
              <div className="section-label" style={{ margin: 0 }}>
                Configurable parameters ({shown.length})
              </div>
              <input
                className="input"
                style={{ maxWidth: 260 }}
                placeholder="filter parameters…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            </div>
            <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
              These are the registers this controller exposes as writable over Modbus. The full
              configuration tree — crank &amp; warm-up timers, shutdown thresholds, service
              intervals, voltage/frequency windows — is not Modbus-writable; edit those with a
              full-configuration (Config Suite) session. Module internals (passwords, comms link,
              PLC scratch) are intentionally not listed.
            </p>
            {grouped.map(([group, list]) => (
              <div key={group} style={{ marginTop: 8 }}>
                {group !== '_' && <div className="param-group-head">{group}</div>}
                <div className="param-list">
                  {list.map((p) => (
                    <div className="param-row" key={p.address}>
                      <div className="param-name">
                        <div className="title">{p.label}</div>
                        <div className="meta">
                          <span className="mono">@{p.address}</span>
                          {p.min !== null || p.max !== null
                            ? ` · range ${p.min ?? '–'}…${p.max ?? '–'}`
                            : ''}
                        </div>
                      </div>
                      <div className="param-current mono">
                        {p.current}
                        {p.unit ? ` ${p.unit}` : ''}
                      </div>
                      <div className="param-write">
                        <input
                          className="input mono"
                          style={{ width: 90 }}
                          inputMode="numeric"
                          placeholder="value"
                          value={edit[p.address] ?? ''}
                          onChange={(e) => setEdit((s) => ({ ...s, [p.address]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void write(p);
                          }}
                        />
                        <button className="btn btn-ghost btn-sm" onClick={() => void write(p)}>
                          Write
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="panel panel-pad">
            <div className="section-label">Session transcript</div>
            {log.length === 0 ? (
              <p className="faint" style={{ margin: 0 }}>
                No writes yet.
              </p>
            ) : (
              <div className="stack">
                {log.map((w, i) => (
                  <div className="row-card" key={i}>
                    <div>
                      <div className="title">{w.label}</div>
                      <div className="meta mono">
                        {w.value_before} → {w.value_written}
                        {w.break_glass ? ' · break-glass' : ''}
                      </div>
                    </div>
                    <span className={`pill ${w.verified ? 'good' : 'crit'}`}>
                      <span className="dot" />
                      {w.verified ? 'verified' : 'MISMATCH'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Configuration history — every past session (both types) + its transcript. */}
      <div className="panel panel-pad" style={{ marginTop: 16 }}>
        <div className="section-label">Configuration history</div>
        {history.length === 0 ? (
          <p className="faint" style={{ margin: 0 }}>
            No engineering sessions yet.
          </p>
        ) : (
          <div className="stack">
            {history.map((h) => {
              const det = expanded[h.id];
              const hConfig = h.type === 'config_suite';
              return (
                <div key={h.id}>
                  <div className="row-card clickable" onClick={() => void toggle(h.id)}>
                    <div>
                      <div className="title">
                        {h.reason}{' '}
                        <span className={`tag ${hConfig ? 'tag-config' : 'tag-param'}`}>
                          {typeLabel(h.type)}
                        </span>
                      </div>
                      <div className="meta">
                        {new Date(h.opened_at).toLocaleString()}
                        {h.opened_by ? ` · ${h.opened_by}` : ''}
                        {hConfig
                          ? ' · configuration session'
                          : ` · ${Number(h.write_count)} write${
                              Number(h.write_count) === 1 ? '' : 's'
                            }${
                              Number(h.mismatch_count) > 0 ? ` · ${h.mismatch_count} mismatch` : ''
                            }`}
                      </div>
                    </div>
                    <div className="row-right">
                      <span className={`pill ${h.status === 'open' ? 'warn' : 'idle'}`}>
                        {h.status}
                      </span>
                      <span className="faint">{det ? '▾' : '▸'}</span>
                    </div>
                  </div>
                  {det === 'loading' && (
                    <div className="faint" style={{ padding: '8px 14px' }}>
                      Loading…
                    </div>
                  )}
                  {det && det !== 'loading' && (
                    <div className="param-list" style={{ marginTop: 8 }}>
                      {det.session.type === 'config_suite' ? (
                        !det.artefacts.pre && !det.artefacts.post ? (
                          <div className="faint" style={{ padding: '10px 14px' }}>
                            Configuration session (DSE Configuration Suite). No config snapshot was
                            attached.
                          </div>
                        ) : (
                          <div style={{ padding: '10px 14px' }}>
                            <div className="stack">
                              {(['pre', 'post'] as const).map(
                                (k) =>
                                  det.artefacts[k] && (
                                    <div className="row-card" key={k}>
                                      <div>
                                        <div className="title">
                                          {k === 'pre' ? 'As-found (before)' : 'As-left (after)'}
                                        </div>
                                        <div className="meta mono">
                                          {det.artefacts[k]!.filename} ·{' '}
                                          {fmtBytes(det.artefacts[k]!.byte_size)} · sha256{' '}
                                          {det.artefacts[k]!.sha256.slice(0, 12)}…
                                        </div>
                                      </div>
                                      <button
                                        className="btn btn-ghost btn-sm"
                                        onClick={() => void downloadArtefact(det.artefacts[k]!.id)}
                                      >
                                        Download
                                      </button>
                                    </div>
                                  ),
                              )}
                            </div>
                            {det.artefacts.changed !== null && (
                              <div style={{ marginTop: 10 }}>
                                <span className={`pill ${det.artefacts.changed ? 'warn' : 'good'}`}>
                                  <span className="dot" />
                                  {det.artefacts.changed
                                    ? 'configuration changed'
                                    : 'no change (before = after)'}
                                </span>
                              </div>
                            )}
                          </div>
                        )
                      ) : det.writes.length === 0 ? (
                        <div className="faint" style={{ padding: '10px 14px' }}>
                          No writes in this session.
                        </div>
                      ) : (
                        det.writes.map((w, i) => (
                          <div className="param-row" key={i}>
                            <div className="param-name">
                              <div className="title">{w.label}</div>
                              <div className="meta mono">
                                @{w.address} · {w.value_before} → {w.value_written}
                                {w.break_glass
                                  ? ` · break-glass${w.justification ? `: ${w.justification}` : ''}`
                                  : ''}
                              </div>
                            </div>
                            <div className="param-current mono">→ {w.value_readback}</div>
                            <span className={`pill ${w.verified ? 'good' : 'crit'}`}>
                              <span className="dot" />
                              {w.verified ? 'verified' : 'MISMATCH'}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
